// src/components/AdminNavbar.jsx

import { LogOut, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Admin top navigation bar component with mobile sidebar toggle
 * @param {Object} props - Component props
 * @param {Object} props.user - Current user object
 * @param {Function} props.handleLogout - Logout handler function
 * @param {Function} props.toggleSidebar - Toggle sidebar function
 * @param {boolean} props.sidebarOpen - Whether sidebar is open
 * @returns {JSX.Element} AdminNavbar component
 */
const AdminNavbar = ({ user, handleLogout, toggleSidebar, sidebarOpen }) => {
  return (
    <nav className="bg-slate-900 border-b border-slate-800 shadow-lg relative z-30">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-auto py-2">
          {/* Left Section - Logo and Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 rounded-md text-gray-300 hover:text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-white transition-colors"
            >
              {sidebarOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>

            {/* Logo */}
            <Link to="/dashboard" className="flex items-center space-x-3">
              <img
                src="/logo.svg"
                alt="Patriot Claims"
                className="h-16 w-auto"
              />
            </Link>
          </div>

          {/* Right Section - User Menu */}
          <div className="flex items-center space-x-4">
            {/* Admin Badge */}
            <div className="hidden sm:flex items-center space-x-3">
              <div className="text-right">
                <div className="text-white text-sm font-medium">
                  {user?.displayName || 'Administrator'}
                </div>
                <div className="text-gray-400 text-xs">{user?.email}</div>
              </div>
            </div>

            {/* Profile Picture */}
            <div className="h-10 w-10 bg-slate-700 rounded-full flex items-center justify-center overflow-hidden border-2 border-slate-600">
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt="Profile"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null; // Prevent infinite loop
                    e.target.src =
                      'https://static.vecteezy.com/system/resources/thumbnails/002/318/271/small/user-profile-icon-free-vector.jpg';
                  }}
                />
              ) : (
                <svg
                  className="h-6 w-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              )}
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-gray-300 hover:text-white bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-md transition-colors duration-200 border border-slate-700"
              title="Sign Out"
            >
              <LogOut className="h-4 w-4" />
              <span className="text-sm font-medium hidden sm:block">
                Sign Out
              </span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AdminNavbar;
